import React, { useEffect, useState } from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ProfileState } from 'store/modules/profile_store/types';
import Loader from 'components/atoms/loader';
import createTrack from 'services/actions/tracker_actions';
import { canAccessPriceIntelligence } from 'services/alfa_tools';
import { bloqueioDaAssinatura } from 'services/assinatura';
import BlockedSubscription from 'components/molecules/blocked_subscription';
import BlockedSubscriptionRepresentant from 'components/molecules/blocked_subscription_representant';
import PriceIntelligenceSampleTable from 'components/molecules/price_intelligence_sample_table';
import {
  fmtBRL,
  fmtDataBR,
  fmtInt,
  linkRefazerBusca,
  PERIODO_PERSONALIZADO,
  priceIntelligenceApi,
  rotuloPeriodo,
  SavedSearch,
  unidadeLabel,
} from 'services/price_intelligence';
import { formatarQuantidade, totalEstimado } from 'services/price_intelligence_batch';
import AmbassadorToolFeedback from 'components/molecules/ambassador_tool_feedback';
import { PRICE_INTELLIGENCE_AMBASSADOR_SLUG } from 'services/ambassador_tools';
import { Container } from './styles';

const PriceIntelligenceSavedDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [item, setItem] = useState<SavedSearch | null>(null);
  const { profile } = useSelector<ApplicationState, ProfileState>((s) => s.storage.profile);

  useEffect(() => {
    document.title = 'Pesquisa de Preços Salva - ConLicitação';
    priceIntelligenceApi
      .getSaved(id)
      .then((data) => {
        setItem(data);
        createTrack('Análise de Preços - Abriu pesquisa salva', { query: data.query });
      })
      .catch(() => setErro('Pesquisa não encontrada.'))
      .finally(() => setCarregando(false));
  }, [id]);

  const resumo = item && item.snapshot ? item.snapshot.resumo : null;
  const cesta = (item && item.snapshot && item.snapshot.cesta) || [];
  const curation = item && item.snapshot ? item.snapshot.curation : null;
  const regime = item && item.snapshot ? item.snapshot.regime : undefined;
  const unidade = resumo && resumo.unidade ? ` (R$/${unidadeLabel(resumo.unidade)})` : '';
  const itemDoLote = item && item.item ? item.item : null;
  const total =
    itemDoLote && resumo && resumo.regime !== 'global'
      ? totalEstimado({
          quantidade: itemDoLote.quantidade,
          unidade: itemDoLote.unidade,
          resumo: { valor: resumo.mediana || null, unidade: resumo.unidade || null },
        })
      : null;
  const periodo =
    item && (item.date_from || item.date_to)
      ? rotuloPeriodo({
          periodoMeses: PERIODO_PERSONALIZADO,
          dataDe: item.date_from || '',
          dataAte: item.date_to || '',
        })
      : item && item.period_months
      ? `${item.period_months} meses`
      : null;

  // Fase alfa: acesso restrito a clientes alfa_testers e clientes liberados.
  if (!canAccessPriceIntelligence(profile)) return <Redirect to="/painel" />;
  const bloqueio = bloqueioDaAssinatura(profile);
  if (bloqueio.bloqueada)
    return (
      <div className="page-container">
        {bloqueio.representada ? (
          <BlockedSubscriptionRepresentant />
        ) : (
          <BlockedSubscription paralyzed={bloqueio.paralisada} />
        )}
      </div>
    );

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions">
        <Container>
          <Link to="/pesquisa_de_precos/salvas" className="back-link">
            <i className="material-icons">arrow_back</i>
            Pesquisas salvas
          </Link>

          {carregando && (
            <div className="loading-wrap">
              <Loader />
            </div>
          )}

          {erro && !carregando && (
            <div className="alert alert-danger mt-3" role="alert">
              {erro}
            </div>
          )}

          {item && !carregando && (
            <>
              <div className="saved-head">
                <div>
                  <h4>{item.name}</h4>
                  <p className="saved-sub">
                    {item.query}
                    {periodo ? ` · ${periodo}` : ''} ·{' '}
                    {item.ufs && item.ufs.length ? item.ufs.join(', ') : 'Brasil inteiro'}
                  </p>
                  {item.batch_name && (
                    <p className="saved-batch">
                      <i className="material-icons">folder_open</i>
                      Lote: {item.batch_name}
                    </p>
                  )}
                </div>
                <Link to={linkRefazerBusca(item)} className="btn btn-primary btn-ia">
                  <i className="material-icons align-middle mr-1" style={{ fontSize: 18 }}>
                    refresh
                  </i>
                  Refazer busca
                </Link>
              </div>

              <div className="frozen-note">
                <i className="material-icons">lock_outline</i>
                <span>
                  <strong>Valores congelados em {fmtDataBR(item.created_at)}.</strong> Esta pesquisa
                  preserva as amostras e estatísticas da data da busca. Use “Refazer busca” para
                  recotar com os dados atuais da base.
                </span>
              </div>

              {resumo && (
                <div className="detail-stats">
                  <div className="stat-tile accent">
                    <p className="stat-label">Mediana de fechamento{unidade}</p>
                    <p className="stat-value">{fmtBRL(resumo.mediana)}</p>
                  </div>
                  {itemDoLote && itemDoLote.quantidade !== null ? (
                    <div className="stat-tile">
                      <p className="stat-label">
                        Total estimado ({formatarQuantidade(itemDoLote.quantidade)}{' '}
                        {itemDoLote.unidade || ''})
                      </p>
                      <p className="stat-value">{total !== null ? fmtBRL(total) : '—'}</p>
                    </div>
                  ) : (
                    <div className="stat-tile">
                      <p className="stat-label">Menor preço da cesta{unidade}</p>
                      <p className="stat-value">{fmtBRL(resumo.menor)}</p>
                    </div>
                  )}
                  <div className="stat-tile">
                    <p className="stat-label">Cesta de preços</p>
                    <p className="stat-value">{fmtInt(resumo.n_amostras)} amostras</p>
                  </div>
                  <div className="stat-tile">
                    <p className="stat-label">Concorrentes · órgãos</p>
                    <p className="stat-value">
                      {fmtInt(resumo.fornecedores)} · {fmtInt(resumo.orgaos)}
                    </p>
                  </div>
                </div>
              )}

              {curation && curation.reason && (
                <div className="frozen-note" style={{ marginTop: 14 }}>
                  <i className="material-icons">auto_awesome</i>
                  <span>
                    <strong>Curadoria de IA na data da pesquisa:</strong> manteve{' '}
                    {fmtInt(curation.kept)} de {fmtInt(curation.analyzed)} resultados analisados.{' '}
                    {curation.reason}
                  </span>
                </div>
              )}

              {item.notes && (
                <div className="detail-notes">
                  <strong>Observações</strong>
                  {item.notes}
                </div>
              )}

              <div className="detail-table-card">
                <div className="detail-table-head">
                  <p>Cesta de preços ({fmtInt(cesta.length)})</p>
                </div>
                <PriceIntelligenceSampleTable hits={cesta} regime={regime} />
              </div>
            </>
          )}
        </Container>
      </div>
      <AmbassadorToolFeedback toolSlug={PRICE_INTELLIGENCE_AMBASSADOR_SLUG} />
    </div>
  );
};

export default PriceIntelligenceSavedDetail;
